/**
 * API Client für Publishing Schedules
 * 
 * Endpoints für temporäre Versionen und Scheduling.
 */

import apiClient from '../api-client'
import type {
  SchedulesResponse,
  ScheduleResponse,
  ActiveScheduleResponse,
  CancelScheduleResponse,
  CreateScheduleRequest,
  CreateScheduleForEntityRequest,
  SchedulesQuery,
  ScheduleEntityType,
} from '@/types/publishing'

const BASE_URL = '/api/v1/publishing-schedules'
const CONTENT_BLOCKS_URL = '/api/v1/content-blocks'
const CHANNEL_PACKAGES_URL = '/api/v1/channels'

// =============================================================================
// GENERAL SCHEDULES API
// =============================================================================

/**
 * Get all schedules (with optional filters)
 */
export async function getSchedules(
  query?: SchedulesQuery
): Promise<SchedulesResponse> {
  const params = new URLSearchParams()
  
  if (query?.entity_type) params.append('entity_type', query.entity_type)
  if (query?.entity_id) params.append('entity_id', query.entity_id)
  if (query?.status) {
    const statuses = Array.isArray(query.status) ? query.status : [query.status]
    statuses.forEach(s => params.append('status', s))
  }
  if (query?.from_date) params.append('from_date', query.from_date)
  if (query?.to_date) params.append('to_date', query.to_date)
  
  const queryString = params.toString()
  const url = queryString ? `${BASE_URL}?${queryString}` : BASE_URL
  
  const response = await apiClient.get<SchedulesResponse>(url)
  return response.data
}

/**
 * Get a single schedule
 */
export async function getSchedule(
  scheduleId: string
): Promise<ScheduleResponse> {
  const response = await apiClient.get<ScheduleResponse>(
    `${BASE_URL}/${scheduleId}`
  )
  return response.data
}

/**
 * Create a new schedule (central API)
 */
export async function createSchedule(
  data: CreateScheduleRequest
): Promise<ScheduleResponse> {
  const response = await apiClient.post<ScheduleResponse>(BASE_URL, data)
  return response.data
}

/**
 * Cancel a schedule
 */
export async function cancelSchedule(
  scheduleId: string
): Promise<CancelScheduleResponse> {
  const response = await apiClient.delete<CancelScheduleResponse>(
    `${BASE_URL}/${scheduleId}/cancel`
  )
  return response.data
}

// =============================================================================
// ACTIVE SCHEDULE HELPERS
// =============================================================================

/**
 * Get active schedule for an entity
 */
export async function getActiveSchedule(
  entityType: ScheduleEntityType,
  entityId: string
): Promise<ActiveScheduleResponse> {
  const response = await apiClient.get<ActiveScheduleResponse>(
    `${BASE_URL}/active`,
    {
      params: {
        entity_type: entityType,
        entity_id: entityId,
      },
    }
  )
  return response.data
}

/**
 * Check if entity has active or scheduled schedule
 */
export async function checkScheduleConflict(
  entityType: ScheduleEntityType,
  entityId: string
): Promise<{ has_conflict: boolean; existing_schedule?: ScheduleResponse['schedule'] }> {
  const result = await getSchedules({
    entity_type: entityType,
    entity_id: entityId,
    status: ['scheduled', 'active'],
  })
  
  const activeSchedule = result.schedules.find(
    s => s.status === 'scheduled' || s.status === 'active'
  )
  
  return {
    has_conflict: !!activeSchedule,
    existing_schedule: activeSchedule,
  }
}

// =============================================================================
// CONTENT BLOCK SCHEDULES (Convenience API)
// =============================================================================

/**
 * Create schedule for a content block
 */
export async function createContentBlockSchedule(
  blockId: string,
  data: CreateScheduleForEntityRequest
): Promise<ScheduleResponse> {
  const response = await apiClient.post<ScheduleResponse>(
    `${CONTENT_BLOCKS_URL}/${blockId}/schedules`,
    data
  )
  return response.data
}

/**
 * Cancel content block schedule
 */
export async function cancelContentBlockSchedule(
  blockId: string,
  scheduleId: string
): Promise<CancelScheduleResponse> {
  const response = await apiClient.delete<CancelScheduleResponse>(
    `${CONTENT_BLOCKS_URL}/${blockId}/schedules/${scheduleId}/cancel`
  )
  return response.data
}

// =============================================================================
// CHANNEL PACKAGE SCHEDULES (Convenience API)
// =============================================================================

/**
 * Create schedule for a channel package
 */
export async function createChannelPackageSchedule(
  channelId: string,
  packageId: string,
  data: CreateScheduleForEntityRequest
): Promise<ScheduleResponse> {
  const response = await apiClient.post<ScheduleResponse>(
    `${CHANNEL_PACKAGES_URL}/${channelId}/packages/${packageId}/schedules`,
    data
  )
  return response.data
}

/**
 * Cancel channel package schedule
 */
export async function cancelChannelPackageSchedule(
  channelId: string,
  packageId: string,
  scheduleId: string
): Promise<CancelScheduleResponse> {
  const response = await apiClient.delete<CancelScheduleResponse>(
    `${CHANNEL_PACKAGES_URL}/${channelId}/packages/${packageId}/schedules/${scheduleId}/cancel`
  )
  return response.data
}


